
import accountAPI from '@/api/account';
import { debounce } from 'lodash';
import { AccountTypes, ExternalRoles } from '@/constants/account';

export default {
  name: 'AccountSelector',
  props: {
    value: {
      type: Object | String | Array | null,
    },
    isRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    client: {
      type: Boolean,
      required: false,
      default: false,
    },
    employee: {
      type: Boolean,
      required: false,
      default: false,
    },
    external: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
    },
    groups: {
      type: Array | String,
      required: false,
    },
    excludeGroups: {
      type: Array | String,
      required: false,
    },
    departments: {
      type: Array | String,
      required: false,
    },
    includeAccounts: {
      type: Array | String,
      required: false,
    },
    includePrimaryAddress: {
      type: Boolean,
      required: false,
      default: false,
    },
    labelSize: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
    },
    roles: {
      type: Array,
      required: false,
      default: () => [],
    },
    excludeUuids: {
      type: Array | String,
      default: () => [],
    },
    childAccounts: {
      type: String,
      default: 'include',
      validator: function (value) {
        return ['include', 'exclude', 'only'].includes(value);
      },
    },
    includeExtraFields: {
      type: String | null,
      required: false,
      default: null,
    },

    includeExtraFilters: {
      type: Object | null,
      required: false,
      default: () => {},
    },

    showFormFieldLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      AccountTypes,
      ExternalRoles,
      loading: false,
      optionsList: [],
      accountsPage: 1,
      accountsMeta: [],
    };
  },

  computed: {
    selected: {
      get: function () {
        return this.value;
      },
      set: function (newVal) {
        this.$emit('input', newVal);
      },
    },

    displayLabel() {
      if (this.label) {
        return this.label;
      }

      if (this.external && !this.client && !this.employee) {
        return 'Select introducer';
      } else if (this.employee && !this.client && !this.external) {
        return 'Select employee';
      } else if (this.client && !this.employee && !this.external) {
        return 'Select client';
      } else {
        return 'Select accounts';
      }
    },

    /*
      The accounts endpoint answers with an empty 200 rather than a 403 when the account cannot
      read any of the types being searched, so a picker they have no permission for looks exactly
      like one with no matches. Name the types instead, but only when every type this selector
      searches is off limits - if one of them is readable, results will still come back.
    */
    unsearchableTypes() {
      const searching = [];

      if (this.client) searching.push({ label: 'clients', permission: 'clients:read' });
      if (this.employee) searching.push({ label: 'employees', permission: 'employees:read' });
      if (this.external) searching.push({ label: 'introducers', permission: 'external:read' });

      if (searching.length === 0 || searching.some((type) => this.can(type.permission))) {
        return null;
      }

      return searching.map((type) => type.label).join(' or ');
    },

    unsearchableTypesMessage() {
      return `Your permissions do not let you search ${this.unsearchableTypes}. Ask an administrator if you need access.`;
    },

    displayPlaceholder() {
      if (this.placeholder) {
        return this.placeholder;
      }

      if (this.external && !this.client && !this.employee) {
        return `Start typing an introducer's name to search.`;
      } else if (this.employee && !this.client && !this.external) {
        return `Start typing an employee's name to search.`;
      } else if (this.client && !this.employee && !this.external) {
        return `Start typing a client's name to search.`;
      } else {
        return `Start typing a name to search.`;
      }
    },
  },

  created() {
    if (!this.client) {
      this.grabAccounts();
    }
  },

  mounted() {
    if (!this.client) {
      this.createIntersectionObserver();
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  methods: {
    async grabData(val) {
      if (this.client) {
        await this.grabClients(val);
      } else {
        this.accountsPage = 1;
        this.optionsList = [];

        await this.grabAccounts(val);
      }
    },

    createIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.fetchAccountsNextPage();
          }
        });
      }, options);

      this.observer.observe(this.$refs.loadMoreTrigger);
    },

    async fetchAccountsNextPage() {
      if (this.accountsPage >= this.accountsMeta.last_page) {
        return;
      }

      this.accountsPage++;

      await this.grabAccounts();
    },

    grabClients: debounce(async function (val) {
      let filterType = '';
      if (this.client) filterType = `${filterType},${AccountTypes.Client}`;
      if (this.employee) filterType = `${filterType},${AccountTypes.Employee}`;
      if (this.external) filterType = `${filterType},${AccountTypes.External}`;

      filterType = filterType.slice(1);

      if (val.length >= 3) {
        this.loading = true;

        const params = {
          ...this.getDefaultParams(),
          'filter[name]': val,
          'filter[type]': filterType,
          include: 'primary_email',
        };

        if (this.includePrimaryAddress) {
          params.include += ',primary_address';
        }

        if (this.includeExtraFields) {
          params.include += `,${this.includeExtraFields}`;
        }

        if (this.includeExtraFilters && Object.keys(this.includeExtraFilters).length > 0) {
          for (const [key, value] of Object.entries(this.includeExtraFilters)) {
            params['filter[' + key + ']'] = value;
          }
        }

        if (this.excludeUuids.length > 0) {
          params['filter[exclude_uuid]'] = Array.isArray(this.excludeUuids)
            ? this.excludeUuids.join(',')
            : this.excludeUuids;
        }

        if (this.childAccounts !== 'include') {
          params['filter[is_child]'] = this.childAccounts;
        }

        try {
          const rsp = await accountAPI.getAccounts({ params });
          this.optionsList = [...(rsp?.data?.data || [])];
        } catch (err) {
          console.error('Error fetching clients:', err);
          this.$nuxt.$emit('notification', 'There was an error fetching accounts', 'warning', true);
          this.optionsList = [];
        } finally {
          this.loading = false;
        }
      } else if (!val.length) {
        this.optionsList = [];
      }
    }, 500),

    async grabAccounts(val) {
      this.loading = true;

      let filterType = '';
      if (this.employee) filterType = `${filterType},${AccountTypes.Employee}`;
      if (this.external) filterType = `${filterType},${AccountTypes.External}`;

      filterType = filterType.slice(1);

      const params = {
        ...this.getDefaultParams(),
        'filter[type]': filterType,
        'filter[role]': this.external && !this.employee ? ExternalRoles.Introducer : null,
      };

      if (val) {
        params['filter[name]'] = val;
        this.accountsPage = 1;
      }
      if (!val) params.page = this.accountsPage;

      try {
        const rsp = await accountAPI.getAccounts({ params });

        this.accountsMeta = rsp?.data?.meta || {};

        if (this.accountsMeta?.current_page === 1 && val) {
          // if first page or search => replace data
          this.optionsList = [...(rsp?.data?.data || [])];
        } else {
          // append new data if infinite scroll
          this.optionsList = [...this.optionsList, ...(rsp?.data?.data || [])];
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
        this.$nuxt.$emit('notification', 'There was an error fetching accounts', 'warning', true);
        this.accountsMeta = {};
      } finally {
        this.loading = false;
      }
    },

    getDefaultParams() {
      let params = {
        per_page: 200,
      };

      if (this.groups) {
        params['filter[groups]'] = Array.isArray(this.groups) ? this.groups.join(',') : this.groups;
      }
      if (this.excludeGroups) {
        params['filter[exclude_groups]'] = Array.isArray(this.excludeGroups)
          ? this.excludeGroups.join(',')
          : this.groups;
      }

      if (this.departments) {
        params['filter[departments]'] = Array.isArray(this.departments) ? this.departments.join(',') : this.departments;
      }

      if (this.includeAccounts) {
        params['filter[include]'] = Array.isArray(this.includeAccounts)
          ? this.includeAccounts.join(',')
          : this.includeAccounts;
      }

      if (this.includePrimaryAddress) {
        params.include = params.include ? `${params.include},primary_address` : 'primary_address';
      }

      if (this.roles.length) {
        params['filter[role]'] = this.roles.join(',');
      }

      return params;
    },
  },
};
